// ---- Area-of-effect hit geometry (special moves) ---------------------------
// Pure, DOM-free, server-free — sector (cone/circle) + line (pierce corridor)
// hit tests, damage falloff, knockback-origin, and the ONE resolve choke for
// specials. Server attack path, Skill Sim, and tests share these so move shapes
// can't drift.
//
// Origin is an explicit AoeOrigin (not hard-wired to a caster record) so future
// delayed / ground-targeted / NPC-cast volumes can pass a frozen cast pose.
// Primary light attack can also resolve through sector shape with falloffMin:1.

import type { SimCandidate } from "./combat.ts";

/** Resolved world-space shape ready for a hit test (weapon reach already applied). */
export type AoeShape =
  | { kind: "sector"; arcDeg: number; range: number }
  | { kind: "line"; length: number; width: number };

/**
 * Cast pose for a volume — feet origin + facing. Separated from "the attacker
 * entity" so delayed telegraphs / ground placements can freeze pose at cast.
 */
export type AoeOrigin = { x: number; z: number; ry: number };

/** One struck candidate with distance used for damage falloff. */
export type AoeHit = {
  id: string;
  x: number;
  z: number;
  /** Sector: XZ distance from origin. Line: distance along the pierce axis. */
  dist: number;
};

/** Fully resolved hit: damage after falloff + knockback origin for applyEffects. */
export type ResolvedAoeHit = AoeHit & {
  dmg: number;
  kbFrom: { x: number; z: number };
};

/** Default min damage fraction at max range (content `falloffMin` overrides). */
export const DEFAULT_FALLOFF_MIN = 0.5;

/** True when a sector is a full 360° circle (shared by hit tests + VFX). */
export function isFullCircle(arcDeg: number): boolean {
  return arcDeg >= 360 - 1e-6;
}

/** Forward XZ unit from yaw `ry` — same convention as movement / facingDelta. */
export function forwardXZ(ry: number): { fx: number; fz: number } {
  return { fx: Math.sin(ry), fz: Math.cos(ry) };
}

/** Absolute angle between facing `ry` and direction (dx,dz), wrapped to [0, π]. */
function facingDelta(ry: number, dx: number, dz: number): number {
  const dA = ((Math.atan2(dx, dz) - ry + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
  return Math.abs(dA);
}

/**
 * Falloff axis max: sector radius, or corridor **length** (along-axis dist).
 * NOT the candidate query radius for lines (see shapeQueryRadius).
 */
export function shapeMaxRange(shape: AoeShape): number {
  return shape.kind === "sector" ? shape.range : shape.length;
}

/**
 * Euclidean radius for candidate pre-filters (`inReach` / client feel).
 * Sector = range. Line = hypotenuse of length × half-width so tip-corners of
 * the corridor are never dropped before lineHits runs.
 */
export function shapeQueryRadius(shape: AoeShape): number {
  if (shape.kind === "sector") return shape.range;
  const halfW = shape.width * 0.5;
  return Math.hypot(shape.length, halfW);
}

/**
 * Linear damage falloff: full at dist 0 → `falloffMin` × base at maxRange.
 * Effects (CC) are NOT scaled — callers apply them at full strength.
 */
export function damageFalloff(
  base: number,
  dist: number,
  maxRange: number,
  falloffMin: number = DEFAULT_FALLOFF_MIN,
): number {
  if (!(base > 0)) return 0;
  if (!(maxRange > 0)) return base;
  const t = Math.min(1, Math.max(0, dist / maxRange));
  const min = Math.min(1, Math.max(0, falloffMin));
  return base * (1 + (min - 1) * t);
}

/**
 * Sector hits (cone when arcDeg < 360, circle when 360). Returns `dist` for
 * falloff. Id-sorted (rng-free). Origin is explicit (cast pose).
 */
export function sectorHits(
  origin: AoeOrigin,
  arcDeg: number,
  range: number,
  candidates: SimCandidate[],
): AoeHit[] {
  const half = (arcDeg * Math.PI) / 360;
  const fullCircle = isFullCircle(arcDeg);
  const out: AoeHit[] = [];
  for (const c of candidates) {
    if (c.down) continue;
    const dx = c.x - origin.x, dz = c.z - origin.z;
    const d = Math.hypot(dx, dz);
    if (d > range) continue;
    if (!fullCircle && d > 1e-9 && facingDelta(origin.ry, dx, dz) > half) continue;
    out.push({ id: c.id, x: c.x, z: c.z, dist: d });
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * Pierce corridor: length along facing, full width lateral. Multi-hit — every
 * living candidate whose projection on the axis is in [0, length] and lateral
 * offset ≤ width/2. dist = along-axis projection (for falloff).
 */
export function lineHits(
  origin: AoeOrigin,
  length: number,
  width: number,
  candidates: SimCandidate[],
): AoeHit[] {
  if (!(length > 0) || !(width > 0)) return [];
  const { fx, fz } = forwardXZ(origin.ry);
  const halfW = width * 0.5;
  const out: AoeHit[] = [];
  for (const c of candidates) {
    if (c.down) continue;
    const dx = c.x - origin.x, dz = c.z - origin.z;
    const along = dx * fx + dz * fz;
    if (along < 0 || along > length) continue;
    const lat = Math.abs(dx * (-fz) + dz * fx); // |cross| in 2D
    if (lat > halfW) continue;
    out.push({ id: c.id, x: c.x, z: c.z, dist: along });
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Dispatch sector vs line against an explicit cast origin. */
export function shapeHits(
  origin: AoeOrigin,
  shape: AoeShape,
  candidates: SimCandidate[],
): AoeHit[] {
  if (shape.kind === "sector") {
    return sectorHits(origin, shape.arcDeg, shape.range, candidates);
  }
  return lineHits(origin, shape.length, shape.width, candidates);
}

/**
 * Point used as knockback `from` so applyKnockback's radial math yields the
 * right direction: sector/circle = cast origin (radial); line = behind the
 * victim along facing so the shove is along the pierce axis.
 */
export function knockbackFrom(
  shape: AoeShape,
  origin: AoeOrigin,
  victim: { x: number; z: number },
): { x: number; z: number } {
  if (shape.kind === "sector") {
    return { x: origin.x, z: origin.z };
  }
  const { fx, fz } = forwardXZ(origin.ry);
  return { x: victim.x - fx, z: victim.z - fz };
}

/**
 * THE specials choke: shape hits + falloff damage + knockback origin per target.
 * Callers build eligibility candidates outside; this only applies pure geometry
 * + economy numbers. Light attacks use a sector shape with falloffMin: 1.
 */
export function resolveShapeHits(
  origin: AoeOrigin,
  shape: AoeShape,
  candidates: SimCandidate[],
  opts: { baseDmg: number; falloffMin?: number },
): ResolvedAoeHit[] {
  const falloffMin = opts.falloffMin ?? DEFAULT_FALLOFF_MIN;
  const maxRange = shapeMaxRange(shape);
  const hits = shapeHits(origin, shape, candidates);
  const out: ResolvedAoeHit[] = [];
  for (const h of hits) {
    out.push({
      ...h,
      dmg: damageFalloff(opts.baseDmg, h.dist, maxRange, falloffMin),
      kbFrom: knockbackFrom(shape, origin, h),
    });
  }
  return out;
}
